import { inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { GuideStep } from './guide-step.enum';
import { GuideStepConfig } from './guide.models';

const IS_GUIDE_SHOWN_KEY = 'par_is_guide_shown';

@Injectable({
  providedIn: 'root',
})
export class GuideService {
  private readonly _matDialog = inject(MatDialog);

  readonly isActive = signal(false);
  readonly currentStep = signal<GuideStep | null>(null);
  readonly activeElementRect = signal<DOMRect | null>(null);

  /** Emitted when user navigates forward past all steps (tour completed) */
  readonly completed$ = new Subject<void>();
  /** Emitted when the service needs the map controller to open the mock parking sheet */
  readonly openMockSheet$ = new Subject<void>();
  /** Emitted when sheet should be closed (navigation left sheet zone or tour stopped) */
  readonly closeMockSheet$ = new Subject<void>();

  /** Steps that require the mock bottom sheet to be open */
  readonly sheetSteps = new Set<GuideStep>([GuideStep.SHEET_NAVIGATE, GuideStep.SHEET_REVIEWS]);

  readonly steps: GuideStep[] = [
    GuideStep.MENU,
    GuideStep.ADD_PARKING,
    GuideStep.FILTER,
    GuideStep.ADDRESS_SEARCH,
    GuideStep.FIND_NEAREST,
    GuideStep.MAP_POINT,
    GuideStep.SHEET_NAVIGATE,
    GuideStep.SHEET_REVIEWS,
  ];

  readonly configs: Record<GuideStep, GuideStepConfig> = {
    [GuideStep.MENU]: {
      label: 'Tu otworzysz menu główne z ustawieniami i przewodnikiem',
      yPosition: 'top',
      xAlign: 'start',
    },
    [GuideStep.ADD_PARKING]: {
      label: 'Kliknij tutaj, żeby dodać nowy darmowy parking do mapy',
      yPosition: 'top',
      xAlign: 'end',
    },
    [GuideStep.FILTER]: {
      label: 'Tu możesz filtrować wyświetlane parkingi (np. tylko dobrze oceniane)',
      yPosition: 'top',
      xAlign: 'end',
    },
    [GuideStep.ADDRESS_SEARCH]: {
      label: 'Wyszukaj adres lub miejsce, żeby szybko znaleźć parkingi w okolicy',
      yPosition: 'bottom',
      xAlign: 'start',
    },
    [GuideStep.FIND_NEAREST]: {
      label: 'Kliknij tutaj, żeby znaleźć najbliższy parking od Twojej lokalizacji',
      yPosition: 'bottom',
      xAlign: 'end',
    },
    [GuideStep.MAP_POINT]: {
      label: 'Tak wygląda punkt parkingu na mapie – kliknij go, żeby zobaczyć szczegóły',
      yPosition: 'bottom',
      xAlign: 'center',
    },
    [GuideStep.SHEET_NAVIGATE]: {
      label: 'Kliknij Nawiguj, żeby otworzyć trasę do parkingu w Mapach Google',
      yPosition: 'bottom',
      xAlign: 'start',
      fixedStyle: { bottom: '95px', left: '20px' },
    },
    [GuideStep.SHEET_REVIEWS]: {
      label: 'Tu zobaczysz i dodasz opinie innych użytkowników o tym parkingu',
      yPosition: 'bottom',
      xAlign: 'center',
      fixedStyle: { bottom: '95px', left: '50%', transform: 'translateX(-50%)' },
    },
  };

  private get _currentIndex(): number {
    const step = this.currentStep();
    if (step === null) return -1;
    return this.steps.indexOf(step);
  }

  get isFirstStep(): boolean {
    return this._currentIndex === 0;
  }

  get isLastStep(): boolean {
    return this._currentIndex === this.steps.length - 1;
  }

  stepConfig(step: GuideStep): GuideStepConfig {
    return this.configs[step];
  }

  setActiveElement(rect: DOMRect): void {
    this.activeElementRect.set(rect);
  }

  start(): void {
    this.isActive.set(true);
    this._goToIndex(0, 'forward');
  }

  next(): void {
    const nextIndex = this._currentIndex + 1;
    if (nextIndex < this.steps.length) {
      this._goToIndex(nextIndex, 'forward');
    } else {
      this.complete();
    }
  }

  prev(): void {
    const currentStep = this.currentStep();

    // When going back from sheet zone, close sheet and jump to MAP_POINT
    if (currentStep !== null && this.sheetSteps.has(currentStep)) {
      this.closeMockSheet$.next();
      const mapPointIndex = this.steps.indexOf(GuideStep.MAP_POINT);
      this._goToIndex(mapPointIndex, 'back');
      return;
    }

    const prevIndex = this._currentIndex - 1;
    if (prevIndex >= 0) {
      this._goToIndex(prevIndex, 'back');
    } else {
      this.stop();
      import('./guide-dialog/guide-dialog.component').then(({ GuideDialogComponent }) => {
        this._matDialog.open(GuideDialogComponent, {
          autoFocus: false,
        });
      });
    }
  }

  /** User completed all steps intentionally */
  complete(): void {
    const currentStep = this.currentStep();
    if (currentStep !== null && this.sheetSteps.has(currentStep)) {
      this.closeMockSheet$.next();
    }
    this.isActive.set(false);
    this.currentStep.set(null);
    this.activeElementRect.set(null);
    this.completed$.next();
  }

  stop(): void {
    const currentStep = this.currentStep();
    if (currentStep !== null && this.sheetSteps.has(currentStep)) {
      this.closeMockSheet$.next();
    }
    this.isActive.set(false);
    this.currentStep.set(null);
    this.activeElementRect.set(null);
  }

  private _goToIndex(index: number, direction: 'forward' | 'back'): void {
    this.activeElementRect.set(null);
    const step = this.steps[index];

    if (step === GuideStep.SHEET_NAVIGATE && direction === 'forward') {
      this.openMockSheet$.next();
    }

    this.currentStep.set(step);
  }

  showGuideForNewUsers(): void {
    const isGuideShown = localStorage.getItem(IS_GUIDE_SHOWN_KEY);
    if (!isGuideShown) {
      import('./guide-dialog/guide-dialog.component').then(({ GuideDialogComponent }) => {
        this._matDialog.open(GuideDialogComponent, {
          autoFocus: false,
        });
      });
      localStorage.setItem(IS_GUIDE_SHOWN_KEY, 'true');
    }
  }
}
